import { Box, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConfirmedTransferLuggage } from './TransferLuggageSection';

import type { TravelBooking } from '../queries';
import {
  bookingOptionsDisplay,
  ReservedBookingOptionsPresentation,
  SelectedBookingOptionsPresentation,
} from '../selectedBookingOptions';

interface Props {
  booking: Pick<TravelBooking, 'vertical' | 'selectedOptions' | 'reservedOptions'>;
  compact?: boolean;
}

const LEG_LABEL_KEY: Record<'outbound' | 'return', string> = {
  outbound: 'travelServices.booking.outbound',
  return: 'travelServices.booking.return',
};

function OptionValue({ children }: { children: string }) {
  return (
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  );
}

/**
 * Lo que el proveedor CONFIRMÓ que quedó reservado (con nombre y cantidad):
 * fuente preferida. `accommodations: []` es un resultado real ("el proveedor
 * no asignó plaza"), no ausencia de dato — nunca se confunde con no tener
 * `reservedOptions` en absoluto (eso cae al fallback de abajo).
 */
function ReservedOptions({
  presentation,
  compact,
  hideEmptyAccommodations = false,
}: {
  presentation: ReservedBookingOptionsPresentation;
  compact: boolean;
  hideEmptyAccommodations?: boolean;
}) {
  const { t } = useTranslation();
  const showAccommodations = !hideEmptyAccommodations || presentation.accommodations.length > 0;
  if (!showAccommodations && !presentation.ticketTypeName) return null;
  const accommodationValue =
    presentation.accommodations
      .map(({ name, quantity, leg }) => {
        const base = quantity > 1 ? `${quantity} × ${name}` : name;
        return leg ? `${base} (${t(LEG_LABEL_KEY[leg])})` : base;
      })
      .join(', ') || t('travelServices.selectedOptions.noReservedAccommodations');

  return (
    <Box>
      {!compact && (
        <Typography variant="subtitle2" gutterBottom>
          {t('travelServices.selectedOptions.sectionReserved')}
        </Typography>
      )}
      <Stack spacing={compact ? 0.25 : 0.75}>
        {showAccommodations && <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {t('travelServices.selectedOptions.reservedAccommodations')}
          </Typography>
          <OptionValue>{accommodationValue}</OptionValue>
        </Box>}
        {presentation.ticketTypeName && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('travelServices.selectedOptions.reservedTicketType')}
            </Typography>
            <OptionValue>{presentation.ticketTypeName}</OptionValue>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Sin confirmación del proveedor todavía (booking anterior a este campo, o
 * vertical sin opciones por oferta): lo único que hay es lo que el AGENTE
 * pidió, reconstruido del request — sin nombres, y puede no coincidir con lo
 * que finalmente quedó reservado.
 */
function RequestedOptions({
  presentation,
  compact,
  note,
}: {
  presentation: SelectedBookingOptionsPresentation;
  compact: boolean;
  note?: string;
}) {
  const { t } = useTranslation();
  const accommodationValue =
    presentation.kind === 'ferry'
      ? presentation.accommodations.map(({ id, count }) => `${id} × ${count}`).join(', ') ||
        t('travelServices.selectedOptions.noFerryAccommodations')
      : presentation.usesProviderSeatAssignment
        ? t('travelServices.selectedOptions.noExplicitBusSeats')
        : presentation.seatIds.join(', ');
  const accommodationLabel =
    presentation.kind === 'ferry'
      ? t('travelServices.selectedOptions.ferryAccommodations')
      : t('travelServices.selectedOptions.busSeats');
  const ticketTypeLabel =
    presentation.kind === 'ferry'
      ? t('travelServices.selectedOptions.ferryTicketType')
      : t('travelServices.selectedOptions.busClassOverride');

  return (
    <Box>
      {!compact && (
        <Typography variant="subtitle2" gutterBottom>
          {t('travelServices.selectedOptions.section')}
        </Typography>
      )}
      {note && (
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          {note}
        </Typography>
      )}
      <Stack spacing={compact ? 0.25 : 0.75}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {accommodationLabel}
          </Typography>
          <OptionValue>{accommodationValue}</OptionValue>
        </Box>
        {presentation.ticketTypeId && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {ticketTypeLabel}
            </Typography>
            <OptionValue>{presentation.ticketTypeId}</OptionValue>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export function SelectedBookingOptions({ booking, compact = false }: Props) {
  const { t } = useTranslation();
  const display = bookingOptionsDisplay(booking);
  if (booking.vertical === 'transfers' && booking.reservedOptions?.luggage) {
    return <ConfirmedTransferLuggage luggage={booking.reservedOptions.luggage} />;
  }

  switch (display.mode) {
  case 'reserved':
    return (
      <ReservedOptions
        presentation={display.reserved}
        compact={compact}
        hideEmptyAccommodations={booking.vertical === 'tours'}
      />
    );
  case 'requested':
    return <RequestedOptions presentation={display.requested} compact={compact} />;
  case 'reserved-and-requested': {
    // El proveedor no confirmó (al menos) una faceta pedida: se muestran
    // ambas fuentes para que la discrepancia quede visible, en vez de que
    // la UI la resuelva en silencio ocultando lo pedido. La nota tiene que
    // nombrar la faceta que realmente falta — nunca una genérica que el
    // panel de arriba contradiga (ej. "ninguna plaza" cuando SÍ hay una
    // acomodación confirmada y lo que falta es la tarifa).
    const { accommodations, ticketType } = display.missing;
    const noteKey =
      accommodations && ticketType
        ? 'travelServices.selectedOptions.requestedDiscrepancyNoteBoth'
        : accommodations
          ? 'travelServices.selectedOptions.requestedDiscrepancyNoteAccommodations'
          : 'travelServices.selectedOptions.requestedDiscrepancyNoteTicketType';
    return (
      <Stack spacing={compact ? 1 : 1.5}>
        <ReservedOptions presentation={display.reserved} compact={compact} />
        <Divider />
        <RequestedOptions presentation={display.requested} compact={compact} note={t(noteKey)} />
      </Stack>
    );
  }
  case 'none':
  default:
    return null;
  }
}
